/**
 * Button classes shared by the site navbar and the job editor, built on the
 * Liquid Glass controls in app/styles/tailwind.css: a cobalt glass capsule
 * for the one primary action, ghost glass capsules for the rest, and icons
 * nested in their own disc.
 *
 * The material draws the pressed and disabled states itself (an inner light,
 * a desaturated fill), so these carry no opacity or transform utilities for
 * them: opacity would fade the glass. They only add the not-allowed cursor.
 *
 * The material declares no transition, and a duration alone would animate
 * every property, the press light's box-shadow included, so the capsules
 * animate their colours only (`transition-colors`).
 *
 * Text on white uses `signal-deep`: `signal-accent` fails WCAG AA for small
 * text, so it is reserved for fills, rings and icons.
 */

/** Spring-like ease shared by every Orbit transition. */
export const ORBIT_EASE = "ease-[cubic-bezier(0.16,1,0.3,1)]";

/**
 * Primary glass capsule. Buttons that need their own glow add a `shadow-*`
 * utility, which overrides the component's shadow.
 */
export const PRIMARY_PILL_BASE =
  "btn-glass-primary group gap-2 transition-colors duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] disabled:cursor-not-allowed";

/** Primary action capsule: publish, register, confirm. */
export const PRIMARY_PILL = PRIMARY_PILL_BASE;

/**
 * The homepage "Generate campaign" submit. It sits disabled until a URL is
 * typed, so it opts out of the material's greyed disabled fill
 * (`glass-keep-fill`) and stays a full-strength primary capsule.
 */
export const CLAY_PILL =
  "btn-glass-primary glass-keep-fill group gap-2 transition-colors duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] disabled:cursor-not-allowed";

/**
 * Secondary capsule lifted off the surface: visit page, book a demo, log in.
 * Its deep-blue label would beat the material's faded disabled ink, so the
 * disabled label fades here.
 */
export const SECONDARY_PILL =
  "btn-glass-ghost group gap-2 text-signal-deep transition-colors duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] disabled:cursor-not-allowed disabled:text-signal-ink/50";

/** Trailing icon disc nested inside PRIMARY_PILL ("button in button"). */
export const PRIMARY_PILL_ICON =
  "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/15 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-px group-hover:translate-x-0.5 group-hover:scale-105";

/** Trailing icon disc nested inside SECONDARY_PILL. */
export const SECONDARY_PILL_ICON =
  "glass-arrow h-6 w-6 flex-shrink-0 text-signal-deep transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-px group-hover:translate-x-0.5 group-hover:scale-105";

/** Round icon-only control on a surface: close, dismiss, navigate. */
export const ICON_DISC =
  "inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-signal-muted transition-[background-color,color,transform] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-white/50 hover:text-signal-ink active:scale-95 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-signal-accent/25";
